import React from 'react';
import {
  Box,
  Button,
  Divider,
  MenuItem,
  Popover,
  Typography,
} from '@mui/material';
import CreateFavouriteListPopUp, {
  CreateFavouriteListFormValues,
} from './CreateFavouriteListPopUp';
import { useFavouriteList } from 'hooks/Favourites';
import { IconButtonWrapper } from 'sharedComponents/UI';
import { HeartIcon } from 'components/SVG';
import type { LoggedInUserFavouriteListMeta } from 'types/common';
import { Close } from '@mui/icons-material';
import ThreeLineSkeleton from 'sharedComponents/Skeleton/ThreeLineSkeleton';

type FavouritePopUpPropTypes = {
  open: boolean;
  anchorEl: HTMLElement | null;
  existingFavouriteList: LoggedInUserFavouriteListMeta[];
  handleClose: () => void;
  onCreateList: (values: CreateFavouriteListFormValues) => void;
  onFavouriteItemClick: (listID: number) => void;
  isLoading?: boolean;
};

function FavouritePopUp({
  open,
  anchorEl,
  handleClose,
  onCreateList,
  onFavouriteItemClick,
  existingFavouriteList,
  isLoading,
}: FavouritePopUpPropTypes) {
  const { favouriteList, isLoading: isFavouriteListLoading } =
    useFavouriteList();
  const [anchorElCreateList, setAnchorElCreateList] =
    React.useState<HTMLButtonElement | null>(null);
  const [selectedItemID, setSelectedItemID] = React.useState<number | null>(
    null
  );

  const openCreateList = Boolean(anchorElCreateList);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorElCreateList(event.currentTarget);
  };

  const handleCloseCreateList = () => {
    setAnchorElCreateList(null);
  };

  const getIsFavouritedByCurrentSpark = (listID: string): boolean => {
    return existingFavouriteList
      .map((listItem) => listItem.id)
      .includes(Number(listID));
  };

  const handleFavouriteItemClick = (listID: number) => {
    setSelectedItemID(listID);
    onFavouriteItemClick(listID);
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClick={(e) => e.stopPropagation()}
      onClose={(e) => {
        //@ts-ignore
        e.stopPropagation();
        handleClose();
      }}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      sx={{
        '& .MuiPaper-root': {
          borderRadius: '15px',
          marginTop: '28px',
        },
      }}
    >
      <Box
        display={'flex'}
        width='25rem'
        flexDirection={'column'}
        paddingTop={2}
      >
        <Box display={'flex'} justifyContent={'space-between'}>
          <Typography
            variant='h5'
            sx={{
              fontFamily: 'Mont-SemiBold',
              marginLeft: '20px',
            }}
          >
            Favourites
          </Typography>
          <Close
            onClick={handleClose}
            sx={{
              cursor: 'pointer',
              marginRight: '15px',
              marginTop: '2px',
            }}
          />
        </Box>
        <Box
          component='div'
          display={'flex'}
          alignItems={'center'}
          gap={1}
          padding={1}
          sx={{ cursor: 'pointer' }}
        >
          {openCreateList && (
            <CreateFavouriteListPopUp
              open={openCreateList}
              handleClose={handleCloseCreateList}
              anchorEl={anchorElCreateList}
              onCreateList={onCreateList}
            />
          )}
          <Button
            onClick={handleClick}
            variant='text'
            sx={{
              display: 'flex',
              gap: 1,
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Box
              width={30}
              height={30}
              bgcolor={(theme) => theme.palette.primary.main}
              borderRadius={'50%'}
              display={'flex'}
              justifyContent={'center'}
              alignItems={'center'}
            >
              <Typography
                variant='h5'
                fontSize={30}
                color='white'
                marginTop={0.6}
              >
                +
              </Typography>
            </Box>
            <Typography
              variant='body2'
              sx={{ fontFamily: 'Mont-SemiBold', fontSize: '16px' }}
            >
              Create list
            </Typography>
          </Button>
        </Box>
        <Divider />
        {isFavouriteListLoading && <ThreeLineSkeleton />}
        {favouriteList?.map((list) => (
          <MenuItem onClick={() => handleFavouriteItemClick(Number(list.id))}>
            {getIsFavouritedByCurrentSpark(list?.id) ? (
              <IconButtonWrapper
                sx={{ background: '#FFECEC' }}
                isLoading={selectedItemID === Number(list.id) && isLoading}
              >
                <HeartIcon color='#EF5251' />
              </IconButtonWrapper>
            ) : (
              <IconButtonWrapper
                isLoading={selectedItemID === Number(list.id) && isLoading}
              >
                <HeartIcon />
              </IconButtonWrapper>
            )}
            <Typography variant='body1' fontSize={17}>
              {list.attributes.name}
            </Typography>
          </MenuItem>
        ))}
      </Box>
    </Popover>
  );
}

export default FavouritePopUp;
